import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { adminCompaniesService } from '../services/admin-companies.service';
import { AdminCompany } from '../types';
import { api } from '../../../../core/utils/apiClient';
import { DocumentViewerModal } from '../../documents/components/DocumentViewerModal';
import { AdminDocument } from '../../documents/types';
import { COLORS } from '../../../../core/constants/colors';
import { RecommendationsSection } from '../../../recommendations/components/RecommendationsSection';
import { linkedInEnrichmentService } from '../../../linkedin_enrichment/services/linkedin-enrichment.service';
import { LinkedInEnrichmentData } from '../../../linkedin_enrichment/types';
import { webEnrichmentService } from '../../../web_enrichment/services/web-enrichment.service';
import { WebEnrichmentData } from '../../../web_enrichment/types';
import { CompanyMembersManager } from '../../../dashboard_user/components/CompanyMembersManager';
import { EditLeadershipTab } from '../components/EditLeadershipTab';
import { ProfileCompletenessInfo, EnrichmentScoreInfo } from '../components/CompletenessInfo';
import { AdminOverrideTab } from '../components/AdminOverrideTab';
import { useLeadership } from '../hooks/useLeadership';
import { EnrichmentActionModal } from '../components/EnrichmentActionModal';
import { enrichmentService } from '../../../enrichment/services/enrichment.service';
import { SectorSelect } from '../../../../core/components/SectorSelect';
import { useSectors } from '../hooks/useSectors';
import { QualificationSelect } from '../../../../core/components/QualificationSelect';
import { useQualifications } from '../hooks/useQualifications';
import { LinkedInRecentPosts } from '../../../companies/components/LinkedInRecentPosts';
import { LinkedInProfileDisplay } from '../../../companies/components/LinkedInProfileDisplay';
import { WebEnrichmentDisplay } from '../../../companies/components/WebEnrichmentDisplay';
import { WebOverridePendingReview } from '../../../companies/components/WebOverridePendingReview';

interface Query {
  id: number;
  query_id?: number;
  query_text: string;
  answer?: string;
  answer_preview?: string;
  response_text?: string;
  status: 'success' | 'failed' | 'timeout';
  user_id?: string;
  company_id?: number | null;
  response_time_ms?: number;
  tokens_used?: number | null;
  prompt_tokens?: number;
  completion_tokens?: number;
  top_k_used?: number;
  chunks_retrieved?: number;
  source_documents?: any[];
  sources?: any[];
  error_message?: string;
  error_type?: string;
  model_used?: string;
  embedding_model_used?: string;
  created_at?: string;
  completed_at?: string;
}

export const CompanyDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<AdminCompany | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [queryText, setQueryText] = useState('');
  const [isSubmittingQuery] = useState(false);
  const [queryHistory, setQueryHistory] = useState<Query[]>([]);
  const [, setDocuments] = useState<any[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<AdminDocument | null>(null);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [linkedInData, setLinkedInData] = useState<LinkedInEnrichmentData | null>(null);
  const [isLoadingLinkedIn, setIsLoadingLinkedIn] = useState(false);
  const [webEnrichmentData, setWebEnrichmentData] = useState<WebEnrichmentData | null>(null);
  const [isLoadingWebEnrichment, setIsLoadingWebEnrichment] = useState(false);
  const [showMembersManager, setShowMembersManager] = useState(false);
  const [isEditingLeadership, setIsEditingLeadership] = useState(false);
  const [showEnrichMenu, setShowEnrichMenu] = useState(false);
  const [enrichModal, setEnrichModal] = useState<{ type: 'web' | 'linkedin'; open: boolean }>({ type: 'web', open: false });
  const [scoringInProgress, setScoringInProgress] = useState(false);
  const [isSavingSector, setIsSavingSector] = useState(false);
  const [sectorSaveError, setSectorSaveError] = useState<string | null>(null);
  const [isSavingQualification, setIsSavingQualification] = useState(false);
  const [qualificationSaveError, setQualificationSaveError] = useState<string | null>(null);

  const { sectors } = useSectors();
  const { qualifications } = useQualifications();

  const handleSectorChange = async (sectorId: number | null) => {
    if (!company) return;
    setIsSavingSector(true);
    setSectorSaveError(null);
    try {
      const updated = await adminCompaniesService.updateCompany(company.id, { sector_id: sectorId });
      if (updated) {
        await loadCompanyData();
      } else {
        setSectorSaveError('Failed to save sector. Please try again.');
      }
    } catch {
      setSectorSaveError('Failed to save sector. Please try again.');
    } finally {
      setIsSavingSector(false);
    }
  };

  const handleQualificationChange = async (qualificationId: number | null) => {
    if (!company) return;
    setIsSavingQualification(true);
    setQualificationSaveError(null);
    try {
      const updated = await adminCompaniesService.updateCompany(company.id, { qualification_id: qualificationId });
      if (updated) {
        await loadCompanyData();
      } else {
        setQualificationSaveError('Failed to save qualification. Please try again.');
      }
    } catch {
      setQualificationSaveError('Failed to save qualification. Please try again.');
    } finally {
      setIsSavingQualification(false);
    }
  };

  // Leadership data from the API (manual + enrichment-sourced entries)
  const { leaders: apiLeaders, isLoading: isLoadingLeaders } = useLeadership(company?.id ?? 0);

  const normalizeQuery = useCallback(
    (raw: any, fallbackQueryText?: string): Query => {
      const normalizedAnswer = raw?.answer ?? raw?.response_text ?? raw?.answer_preview ?? '';
      const timestamp = raw?.created_at ?? new Date().toISOString();
      return {
        id: raw?.id ?? raw?.query_id ?? Date.now(),
        query_id: raw?.query_id,
        query_text: raw?.query_text ?? fallbackQueryText ?? '',
        answer: raw?.answer,
        answer_preview: raw?.answer_preview ?? normalizedAnswer,
        response_text: raw?.response_text ?? normalizedAnswer,
        status: raw?.status ?? 'success',
        user_id: raw?.user_id,
        company_id: raw?.company_id ?? (id ? parseInt(id) : null),
        response_time_ms: raw?.response_time_ms,
        tokens_used: raw?.tokens_used,
        prompt_tokens: raw?.prompt_tokens,
        completion_tokens: raw?.completion_tokens,
        top_k_used: raw?.top_k_used,
        chunks_retrieved: raw?.chunks_retrieved,
        source_documents: raw?.source_documents,
        sources: raw?.sources,
        error_message: raw?.error_message,
        error_type: raw?.error_type,
        model_used: raw?.model_used,
        embedding_model_used: raw?.embedding_model_used,
        created_at: timestamp,
        completed_at: raw?.completed_at,
      };
    },
    [id]
  );

  const loadCompanyData = useCallback(async () => {
    try {
      setIsLoading(true);

      // Load company details
      const companyData = await adminCompaniesService.getCompanyById(parseInt(id!));
      if (companyData) {
        setCompany(companyData);
      }

      // Load company documents
      const docsResponse = await api.get(`/api/v1/documents/companies/${id}/documents`);
      if (docsResponse.ok) {
        const docsData = await docsResponse.json();
        setDocuments(docsData.documents || []);
      }

      // Load query history for this company
      const historyResponse = await api.get('/api/v1/llm/query/history?limit=10');
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const companyQueries = (historyData || [])
          .filter((q: Query) => q.company_id === parseInt(id!))
          .map((q: Query) => normalizeQuery(q));
        setQueryHistory(companyQueries);
      }

      // Load LinkedIn data for this company
      setIsLoadingLinkedIn(true);
      try {
        const linkedInResponse = await linkedInEnrichmentService.getData(parseInt(id!));
        setLinkedInData(linkedInResponse);
      } catch (error) {
        console.error('Error loading LinkedIn data:', error);
        // LinkedIn data is optional, so we don't fail the entire page load
      } finally {
        setIsLoadingLinkedIn(false);
      }

      // Load Web enrichment data for this company
      setIsLoadingWebEnrichment(true);
      try {
        const webEnrichmentResponse = await webEnrichmentService.getData(parseInt(id!));
        setWebEnrichmentData(webEnrichmentResponse);
      } catch (error) {
        console.error('Error loading web enrichment data:', error);
        // Web enrichment data is optional, so we don't fail the entire page load
      } finally {
        setIsLoadingWebEnrichment(false);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id, normalizeQuery]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  // Poll for active enrichment scoring session
  useEffect(() => {
    if (!id) return;
    const companyId = parseInt(id);
    let interval: ReturnType<typeof setInterval> | null = null;

    const checkSession = async () => {
      try {
        const result = await enrichmentService.getActiveSession(companyId);
        const wasScoring = scoringInProgress;
        setScoringInProgress(result.active);
        // When scoring finishes, refresh company data to get updated scores
        if (wasScoring && !result.active) {
          loadCompanyData();
        }
      } catch {
        // Ignore polling errors
      }
    };

    checkSession();
    interval = setInterval(checkSession, 5000);
    return () => { if (interval) clearInterval(interval); };
  }, [id, scoringInProgress, loadCompanyData]);

  const handleQuerySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = queryText.trim();
    if (!text || !company?.id) return;

    // Hand the question to the chat page scoped to this company. The chat page
    // auto-submits the pendingQuery; the backend scopes the new conversation to
    // companyId so the agent answers about this company specifically.
    setQueryText('');
    navigate('/dashboard/chat', {
      state: {
        pendingQuery: text,
        isNewQuery: true,
        newChat: true,
        companyId: company.id,
      },
    });
  };

  const formatWebsiteUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = ['#713FFF', '#FF5E2E', '#0E81FF', '#8CE58C'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };


  if (isLoading) {
    return (
      <AdminLayout pageTitle="Company Details" pageDescription="View and manage company information">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading company...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!company) {
    return (
      <AdminLayout pageTitle="Company Details" pageDescription="View and manage company information">
        <div className="flex items-center justify-center min-h-screen">
          <p style={{ color: '#EF4444' }}>Company not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Company Management" pageDescription={`${company.name} - View and manage company details`}>
      <div className="min-h-screen bg-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        {/* Header Section */}
        <div className="bg-white border-b" style={{ borderColor: '#E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <button onClick={() => navigate('/admin/companies')} className="hover:underline">
              Company Management
          </button>
            <span>›</span>
              <span className="text-gray-900 font-medium">{company.name}</span>
        </div>

            {/* Company Header */}
            <div className="flex items-start gap-4 mb-6">
                <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
                  style={{ backgroundColor: getInitialColor(company.name) }}
                >
                  {getCompanyInitial(company.name)}
                </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                {company.name}
              </h1>
                    </div>
                <p className="text-sm text-gray-600">
                  {company.industry || 'Industry'} · {company.location || 'Location'} · {company.employees || 'N/A'} Employees
                </p>
                    </div>
              <div className="flex gap-3">
                {/* Enrich Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowEnrichMenu(!showEnrichMenu)}
                    onBlur={() => setTimeout(() => setShowEnrichMenu(false), 150)}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all hover:bg-gray-100"
                    style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Enrich
                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showEnrichMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <button
                        onClick={() => { setShowEnrichMenu(false); navigate(`/admin/enrichment/${id}`); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Document Enrichment
                      </button>
                      <button
                        onClick={() => {
                          setShowEnrichMenu(false);
                          setEnrichModal({ type: 'web', open: true });
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        Web Enrichment
                      </button>
                      <button
                        onClick={() => {
                          setShowEnrichMenu(false);
                          setEnrichModal({ type: 'linkedin', open: true });
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        LinkedIn Enrichment
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowMembersManager(true)}
                  className="px-5 py-2 rounded-lg font-medium transition-all"
                  style={{ backgroundColor: COLORS.admin.primary, color: 'white' }}
                >
                  Manage Members
                </button>
                  </div>
          </div>

            {/* Overview Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {company.location && (
            <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Headquarters</p>
                  <p className="text-sm text-gray-900">{company.location}</p>
            </div>
              )}
              {company.website && (
            <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Website</p>
                  <a
                    href={formatWebsiteUrl(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                >
                    {company.website}
                  </a>
                  </div>
              )}
              {company.founding_year && (
                  <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Founded</p>
                  <p className="text-sm text-gray-900">{company.founding_year}</p>
                  </div>
              )}
              {company.employees && (
                  <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Employees</p>
                  <p className="text-sm text-gray-900">{company.employees}</p>
                  </div>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">
                  Sector{isSavingSector && <span className="ml-1 text-blue-500">Saving…</span>}
                </p>
                <SectorSelect
                  sectors={sectors}
                  value={company.sector_id ?? null}
                  onChange={handleSectorChange}
                  isDisabled={isSavingSector}
                  placeholder="Unassigned"
                  borderColor="#E5E7EB"
                />
                {sectorSaveError && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{sectorSaveError}</p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">
                  VGG Relationship{isSavingQualification && <span className="ml-1 text-blue-500">Saving…</span>}
                </p>
                <QualificationSelect
                  qualifications={qualifications}
                  value={company.qualification_id ?? null}
                  onChange={handleQualificationChange}
                  isDisabled={isSavingQualification}
                  placeholder="Unassigned"
                  borderColor="#E5E7EB"
                />
                {qualificationSaveError && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{qualificationSaveError}</p>
                )}
              </div>
              </div>

            {/* Industry Tags + Qualification Badge */}
            <div className="flex flex-wrap gap-2">
              {company.industry && company.industry.split(',').map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                >
                  {tag.trim()}
                </span>
              ))}
              {company.qualification_name && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                  {company.qualification_name}
                </span>
              )}
            </div>
                    </div>
                  </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2">
              {/* Navigation Tabs */}
              <div className="border-b mb-6" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex gap-6">
                  {['overview', 'about', 'linkedin', 'leadership', 'web-info', 'similar', 'queries'].map((tab) => (
                        <button
                          key={tab}
                      onClick={() => setActiveTab(tab)}
                          className={`pb-3 px-1 text-sm font-medium capitalize transition-colors ${
                        activeTab === tab
                              ? 'text-blue-600 border-b-2'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                      style={activeTab === tab ? { borderBottomColor: COLORS.admin.primary } : {}}
                        >
                      {tab === 'similar' ? 'Similar Companies' :
                       tab === 'queries' ? 'Query History' :
                       tab === 'web-info' ? 'Web Info' :
                       tab === 'overview' ? 'Overview' :
                       tab === 'linkedin' ? 'LinkedIn' : tab}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tab Content */}
              <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Company Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Private/Public Status</p>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              company.is_private === false || (company.is_private === undefined && company.is_active)
                                ? 'bg-green-50 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {company.is_private === false || (company.is_private === undefined && company.is_active) ? 'Public' : 'Private'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center mb-2">
                          <p className="text-xs font-medium text-gray-500">Profile Completeness</p>
                          {company?.id && <ProfileCompletenessInfo companyId={company.id} />}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${company.profile_completeness_score || 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            {company.profile_completeness_score || 0}%
                            {scoringInProgress && (
                              <span
                                className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                                style={{ borderColor: '#3B82F6', borderTopColor: 'transparent' }}
                                title="Enrichment scoring in progress — score may update soon"
                              />
                            )}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center mb-2">
                          <p className="text-xs font-medium text-gray-500">Enrichment Score</p>
                          <EnrichmentScoreInfo
                            companyId={company.id}
                            enrichmentScore={company.enrichment_score ?? null}
                            latestSessionScore={(company as any).latest_session_score ?? null}
                            latestSessionStatus={(company as any).latest_session_status ?? null}
                            scoringInProgress={scoringInProgress}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full transition-all"
                              style={{ width: `${company.enrichment_score || (company as any).latest_session_score || 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            {company.enrichment_score != null
                              ? `${company.enrichment_score}%`
                              : (company as any).latest_session_score != null
                                ? `${Number((company as any).latest_session_score).toFixed(0)}% (pending)`
                                : '—'}
                            {scoringInProgress && (
                              <span
                                className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                                style={{ borderColor: '#9333EA', borderTopColor: 'transparent' }}
                                title="Enrichment scoring in progress — score may update soon"
                              />
                            )}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Company Domain</p>
                        <p className="text-sm text-gray-900">
                          {company.domain || '—'}
                        </p>
                      </div>
                    </div>

                    {/* Admin Override Section */}
                    {company?.id && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <AdminOverrideTab
                          companyId={company.id}
                          company={company}
                          onFieldsUpdated={loadCompanyData}
                          linkedInData={linkedInData}
                          webEnrichmentData={webEnrichmentData}
                        />
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'about' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">About {company.name}</h3>
                    {company.description ? (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-6">
                        {company.description}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic mb-6">No description available for this company.</p>
                    )}


                    {/* Recent Posts from LinkedIn */}
                    {linkedInData?.extracted_data && (
                      <LinkedInRecentPosts extractedData={linkedInData.extracted_data} />
                    )}
                  </div>
                )}

                {activeTab === 'linkedin' && (
                  <div>
                    <div className="flex items-center gap-2 mb-6">
                      <svg className="w-5 h-5" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <h3 className="text-lg font-bold text-gray-900">LinkedIn Profile</h3>
                    </div>
                    <LinkedInProfileDisplay linkedInData={linkedInData} isLoading={isLoadingLinkedIn} accentColor={COLORS.admin.primary} />
                  </div>
                )}


                {activeTab === 'leadership' && (
                      <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Leadership</h3>
                      <button
                        onClick={() => setIsEditingLeadership(!isEditingLeadership)}
                        className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                        style={isEditingLeadership
                          ? { backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' }
                          : { backgroundColor: COLORS.admin.primary, color: 'white' }
                        }
                      >
                        {isEditingLeadership ? 'Done' : 'Edit'}
                      </button>
                    </div>
                    {isEditingLeadership && company?.id ? (
                      <EditLeadershipTab companyId={company.id} />
                    ) : (isLoadingLeaders || isLoadingLinkedIn || isLoadingWebEnrichment) ? (
                      <div className="flex items-center justify-center py-8">
                        <div
                          className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                          style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                        />
                      </div>
                    ) : apiLeaders.length > 0 ? (
                      <div className="space-y-4">
                        {apiLeaders.map((leader) => (
                          <div key={leader.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50 flex items-start gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-bold text-sm">
                              {leader.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-sm mb-1 text-gray-900">{leader.full_name}</p>
                              {leader.title && (
                                <p className="text-xs mb-2 text-gray-600">{leader.title}</p>
                              )}
                              {leader.bio && (
                                <p className="text-xs text-gray-500 mb-2">{leader.bio}</p>
                              )}
                              {leader.linkedin_url && (
                                <a href={leader.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                  View LinkedIn
                                </a>
                              )}
                              <span className="ml-2 text-xs text-gray-400">{leader.source === 'manual' ? 'Manual' : 'Enriched'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (() => {
                      // Fallback: show enrichment-extracted leadership if no API leaders
                      const webLeadership = webEnrichmentData?.extracted_data?.leadership ||
                                           webEnrichmentData?.extracted_data?.leadership_team ||
                                           webEnrichmentData?.extracted_data?.leaders || [];
                      const linkedInLeadership = linkedInData?.extracted_data?.leadership ||
                                                 linkedInData?.extracted_data?.leaders ||
                                                 linkedInData?.extracted_data?.leadership_team || [];
                      const allLeadership = [
                        ...(Array.isArray(webLeadership) ? webLeadership : typeof webLeadership === 'object' ? Object.values(webLeadership) : []),
                        ...(Array.isArray(linkedInLeadership) ? linkedInLeadership : typeof linkedInLeadership === 'object' ? Object.values(linkedInLeadership) : [])
                      ];
                      const uniqueLeadership = allLeadership.filter((leader, index, self) => {
                        const name = typeof leader === 'object' ? leader.name : String(leader);
                        return index === self.findIndex((l) => (typeof l === 'object' ? l.name : String(l)) === name);
                      });
                      return uniqueLeadership.length > 0 ? (
                        <div className="space-y-4">
                          {uniqueLeadership.map((leader: any, index: number) => (
                            <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                              {typeof leader === 'object' ? (
                                <>
                                  {leader.name && <p className="font-medium text-sm mb-1 text-gray-900">{leader.name}</p>}
                                  {leader.title && <p className="text-xs mb-2 text-gray-600">{leader.title}</p>}
                                  {leader.linkedin_url && (
                                    <a href={leader.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View Profile</a>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-gray-900">{String(leader)}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          <p className="text-sm">No leadership data available</p>
                        </div>
                      );
                    })()}
                      </div>
                    )}

                {activeTab === 'web-info' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Website Enrichment Data</h3>
                    {webEnrichmentData &&
                      company?.id &&
                      Object.keys(webEnrichmentData.pending_web_overrides ?? {}).length > 0 && (
                        <WebOverridePendingReview
                          pendingOverrides={webEnrichmentData.pending_web_overrides!}
                          currentValues={webEnrichmentData.extracted_data ?? {}}
                          companyId={company.id}
                          accentColor={COLORS.admin.primary}
                          onResolved={(updated) => setWebEnrichmentData(updated)}
                        />
                      )}
                    <WebEnrichmentDisplay
                      webEnrichmentData={webEnrichmentData}
                      isLoading={isLoadingWebEnrichment}
                      accentColor={COLORS.admin.primary}
                      canEdit={true}
                      companyId={company?.id}
                      onDataUpdated={(updated) => setWebEnrichmentData(updated)}
                    />
                  </div>
                )}


                {activeTab === 'similar' && company.id && (
                  <div>
                    <RecommendationsSection
                      companyId={company.id}
                      companyName={company.name}
                      limit={10}
                      onCompanyClick={(companyId) => navigate(`/admin/companies/${companyId}`)}
                    />
                </div>
                )}

                {activeTab === 'queries' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Query History</h3>
                    {queryHistory.length > 0 ? (
                  <div className="space-y-3">
                        {queryHistory.map((query) => (
                      <div
                            key={query.id}
                            className="p-4 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            style={{ borderColor: '#E5E7EB' }}
                            onClick={() => navigate(`/dashboard/chat/${query.query_id || query.id}`)}
                          >
                            <p className="text-sm font-medium text-gray-900 mb-1">{query.query_text}</p>
                            <p className="text-xs text-gray-500">
                              {query.created_at ? new Date(query.created_at).toLocaleString() : '—'}
                            </p>
                      </div>
                    ))}
                  </div>
                ) : (
                      <p className="text-sm text-gray-500">No queries yet for this company.</p>
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* Right Column - Query Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
                {/* Blue Gradient Bubble */}
                <div className="mb-4 flex justify-center">
                  <div className="relative w-24 h-24">
                    <div 
                      className="absolute inset-0 rounded-full blur-2xl opacity-60"
                            style={{
                        background: `radial-gradient(circle, ${COLORS.admin.primary}40 0%, ${COLORS.admin.primaryLight}50 50%, transparent 70%)`
                      }}
                    />
                    <div 
                      className="absolute inset-3 rounded-full"
                              style={{
                        background: `radial-gradient(circle at 30% 30%, ${COLORS.admin.primaryLight} 0%, ${COLORS.admin.primary}CC 50%, ${COLORS.admin.primary}AA 100%)`,
                        boxShadow: `0 8px 32px ${COLORS.admin.primary}4D, inset 0 -8px 16px rgba(255, 255, 255, 0.3), inset 0 8px 16px ${COLORS.admin.primary}33`
                      }}
                    />
                          </div>
                        </div>

                {/* Centered Heading */}
                <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Ask about {company.name}</h3>

                {/* Query Input */}
                <form onSubmit={handleQuerySubmit} className="mb-6">
                  <div className="relative">
                    <input
                      type="text"
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                      placeholder="Ask me anything..."
                      disabled={isSubmittingQuery}
                      className="w-full px-4 pr-12 py-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#FAFAFA' }}
                  />
                  <button
                    type="submit"
                      disabled={isSubmittingQuery || !queryText.trim()}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: isSubmittingQuery || !queryText.trim() ? '#E5E7EB' : COLORS.admin.primary,
                        color: '#FFFFFF'
                      }}
                  >
                      {isSubmittingQuery ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                    )}
                  </button>
                  </div>
                </form>
              </div>

              {/* LinkedIn Card */}
              {linkedInData && linkedInData.extracted_data && (
                <div className="bg-white rounded-lg border p-6 mt-6" style={{ borderColor: '#E5E7EB' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-5 h-5" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <h3 className="text-lg font-bold text-gray-900">LinkedIn</h3>
                  </div>

                  {/* Helper function to format numbers */}
                  {(() => {
                    const formatNumber = (num: number): string => {
                      if (num >= 1000000) {
                        return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
                      }
                      if (num >= 1000) {
                        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
                      }
                      return num.toString();
                    };

                    const extractedData = linkedInData.extracted_data;
                    const followerCount = extractedData?.follower_count;
                    const jobOpenings = extractedData?.job_openings || [];
                    const leadership = extractedData?.leadership || [];
                    const fundingInfo = extractedData?.funding_info;

                    return (
                      <div className="space-y-4">
                        {/* Stats Row */}
                        <div className="grid grid-cols-2 gap-4">
                          {followerCount !== undefined && followerCount !== null && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Followers</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatNumber(followerCount)}
                              </p>
                            </div>
                          )}
                          {jobOpenings.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Open Jobs</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {jobOpenings.length}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Leadership */}
                        {leadership.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Key Leadership</p>
                            <div className="flex flex-wrap gap-2">
                              {leadership.slice(0, 5).map((leader: any, index: number) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
                                >
                                  {leader.name || leader.title || (typeof leader === 'string' ? leader : JSON.stringify(leader))}
                                </span>
                              ))}
                              {leadership.length > 5 && (
                                <span className="px-2 py-1 rounded text-xs font-medium text-gray-500">
                                  +{leadership.length - 5} more
                                </span>
                                  )}
                            </div>
                          </div>
                        )}

                        {/* Funding Info */}
                        {fundingInfo && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Funding</p>
                            <div className="space-y-1">
                              {fundingInfo.last_round_raised && (
                                <p className="text-sm font-semibold text-gray-900">
                                  {fundingInfo.last_round_raised}
                                    </p>
                                  )}
                              {fundingInfo.last_round_type && (
                                <p className="text-xs text-gray-600">
                                  {fundingInfo.last_round_type}
                                  {fundingInfo.last_round_date && (
                                    <span className="ml-2">
                                      ({new Date(fundingInfo.last_round_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
                                    </span>
                                  )}
                                </p>
                              )}
                              {fundingInfo.rounds && (
                                <p className="text-xs text-gray-600">
                                  {fundingInfo.rounds} funding round{fundingInfo.rounds !== 1 ? 's' : ''}
                                </p>
                              )}
                            </div>
                        </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Specialties */}
                  {(() => {
                    const specialties = linkedInData.extracted_data?.specialties || 
                                       linkedInData.extracted_data?.specialities || 
                                       linkedInData.extracted_data?.specializations || [];
                    const specialtiesList = Array.isArray(specialties) ? specialties : 
                                           typeof specialties === 'string' ? specialties.split(',').map(s => s.trim()) : 
                                           typeof specialties === 'object' ? Object.values(specialties) : [];
                    
                    return specialtiesList.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-gray-900">
                          Specialties
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {specialtiesList.map((specialty: any, index: number) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                            >
                              {typeof specialty === 'object' ? specialty.name || JSON.stringify(specialty) : String(specialty)}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        document={selectedDocument}
        isOpen={isDocumentViewerOpen}
        onClose={() => {
          setIsDocumentViewerOpen(false);
          setSelectedDocument(null);
        }}
      />

      {/* Members Manager Modal */}
      {showMembersManager && company && (
        <CompanyMembersManager
          companyId={company.id}
          currentUserRole="admin"
          onClose={() => setShowMembersManager(false)}
        />
      )}

      {/* Enrichment Action Modal (Web / LinkedIn) */}
      {enrichModal.open && company && (
        <EnrichmentActionModal
          type={enrichModal.type}
          companyId={company.id}
          initialUrl={
            enrichModal.type === 'web'
              ? company.website || ''
              : linkedInData?.linkedin_url || ''
          }
          onClose={() => setEnrichModal({ ...enrichModal, open: false })}
          onComplete={() => loadCompanyData()}
        />
      )}
    </AdminLayout>
  );
};